import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { getThemeSource } from "src/duck/selectors";
import { themeSourceChanged } from "src/duck/actions";
import environment from "src/utils/environment";
import NamedThemeProvider from "./NamedThemeProvider";

const remote = window.require("@electron/remote");
const { isBrowser } = environment;
const { nativeTheme, getCurrentWindow } = remote;
const browserQuery = window.matchMedia("(prefers-color-scheme: dark)");
const mainWindow = isBrowser ? undefined : getCurrentWindow();

function GlobalThemeProvider({ themeSource, children }) {
  const dispatch = useDispatch();
  if (!isBrowser) nativeTheme.themeSource = themeSource;
  const [themeName, setThemeName] = useState(
    (isBrowser ? browserQuery.matches : nativeTheme.shouldUseDarkColors)
      ? "dark"
      : "light"
  );

  useEffect(() => {
    mainWindow?.setBackgroundColor(
      nativeTheme.shouldUseDarkColors ? "#000" : "#fff"
    );
  }, []);

  useEffect(() => {
    function themeSourceChangeHandler() {
      const themename = nativeTheme.shouldUseDarkColors ? "dark" : "light";
      setThemeName(themename);
      if (nativeTheme.themeSource !== themeSource) {
        mainWindow?.setBackgroundColor(
          nativeTheme.shouldUseDarkColors ? "#000" : "#fff"
        );
        dispatch(themeSourceChanged(nativeTheme.themeSource));
      }
    }

    function browserThemeSourceChangeHandler(e) {
      const preferredTheme = e.matches ? "dark" : "light";
      setThemeName(preferredTheme);
      dispatch(themeSourceChanged(preferredTheme));
    }

    if (!isBrowser) nativeTheme.on("updated", themeSourceChangeHandler);
    if (isBrowser && browserQuery.addEventListener) {
      browserQuery.addEventListener("change", browserThemeSourceChangeHandler);
    } else if (isBrowser) {
      browserQuery.addListener(browserThemeSourceChangeHandler);
    }

    return () => {
      if (!isBrowser) nativeTheme.off("updated", themeSourceChangeHandler);
      if (isBrowser && browserQuery.removeEventListener) {
        browserQuery.removeEventListener(
          "change",
          browserThemeSourceChangeHandler
        );
      }
    };
  }, [dispatch, themeSource]);

  useEffect(() => {
    if (isBrowser && themeSource !== themeName) {
      let currentBrowserTheme = themeSource;
      if (currentBrowserTheme === "system") {
        currentBrowserTheme = browserQuery.matches ? "dark" : "light";
      }
      setThemeName(currentBrowserTheme);
    }
  }, [themeName, themeSource]);

  return (
    <NamedThemeProvider themeName={themeName}>{children}</NamedThemeProvider>
  );
}

function mapStateToProps(state) {
  return {
    themeSource: getThemeSource(state)
  };
}

export default connect(mapStateToProps)(GlobalThemeProvider);
